import {
  createSearchParams,
  type DocumentMeta,
  type DocumentVersion,
  type SearchDocumentNames,
  searchDocuments,
  searchDocumentStatus
} from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  collectWidgets,
  isLinkContent,
  LinkContent,
  traverseDocument
} from "@prismicio/types-internal/lib/content"
import { useState } from "react"

import { env } from "../../../app/env"
import { useGoToNewDocument } from "../../../app/routing"
import { baseUrl, type DocumentVersionStatus } from "../../../app/services/core"
import { useRepository } from "../../../app/services/repository"
import type { DocumentData, ExistingDocumentData } from "../../documentData"
import { useDocument } from "../../DocumentProvider"
import { useGoToDocument } from "../../documentRouting"
import type { DocumentDraft } from "../../useDraft"

export function useLocales() {
  const {
    data,
    draft,
    groupLangId,
    locale: sourceLocale,
    status,
    title
  } = useDocument()

  const langs = useRepository().languages

  const [isEnabled, setIsEnabled] = useState(true)
  const [locale, setLocale] = useState(sourceLocale)

  useOnChange(sourceLocale, setLocale)

  const goToDocument = useGoToDocument()
  const goToNewDocument = useGoToNewDocument()

  async function onLocaleChange(localeId: string) {
    // We want synchronous locale switching even while all this (version loading + router navigation)
    // is happening. Otherwise it feels like the user input was not taken into account.
    setLocale(localeId)

    if (!groupLangId) {
      // Should never happen, all existing documents have a groupLangId.
      if (data.type !== "new") return

      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        locale: localeId
      })

      return
    }

    setIsEnabled(false)

    const version = await findLocalizedVersion(groupLangId, localeId, status)

    if (version)
      goToDocument({
        id: version.doc.id,
        status: version.version.status,
        replace: false
      })
    else {
      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        groupLangId,
        locale: localeId
      })
    }

    setIsEnabled(true)
  }

  async function onCopyToLocale(localeId: string) {
    // We want synchronous locale switching even while all this (version loading + router navigation)
    // is happening. Otherwise it feels like the user input was not taken into account.
    setLocale(localeId)

    if (!groupLangId) {
      // Should never happen, all existing documents have a groupLangId.
      if (data.type !== "new") return

      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        locale: localeId
      })

      return
    }

    setIsEnabled(false)

    if (data.type === "new") return

    const initialDraft = await getInitialDraft(data, localeId, status)

    const version = await findLocalizedVersion(groupLangId, localeId, status)

    if (version)
      goToDocument({
        id: version.doc.id,
        replace: false,
        status: version.version.status,
        initialDraft
      })
    else {
      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        groupLangId,
        locale: localeId,
        initialDraft,
        tags: data.type === "existing" ? data.version.tags : undefined,
        title
      })
    }

    setIsEnabled(true)
  }

  return { onLocaleChange, isEnabled, locale, langs, onCopyToLocale }
}

// When we are and stay in the "new doc" screen, animate the content
// to show users their locale change was taken into account.
function animateLocalChange(data: DocumentData, draft: DocumentDraft) {
  if (data.type !== "new") return

  draft.animateNextChange()
  draft.cancel()
}

function findLocalizedVersion(
  groupLangId: string,
  localeId: string,
  status: DocumentVersionStatus
) {
  const searchTerm = new URLSearchParams(
    createSearchParams<SearchDocumentNames>([
      { name: "language", values: [localeId] },
      { name: "group_lang_id", values: [groupLangId] }
    ])
  ).toString()

  // We assume a search by group_lang_id + language is going to turn up
  // very few results so only consider the first page.
  return searchDocuments(baseUrl, env.PAGE_BUILDER_AUTH_STRATEGY, searchTerm)
    .then((response) => findExistingLocaleVersion(response.results, status))
    .catch(() => undefined)
}

// Creates draft of doc with converted doc links
async function getInitialDraft(
  data: ExistingDocumentData,
  localeId: string,
  status: DocumentVersionStatus
) {
  // grabs doc ids inside Link Widgets of type Document
  const docIds = getDocIds(data)

  // If there are no doc links, return original document
  if (docIds.length === 0) return Promise.resolve(data.document)

  const groupLangIds = await getGroupLangIds(docIds, status)

  const translationIds = await getTranslationIds(
    groupLangIds.map((lng) => lng.groupLangId),
    localeId,
    status
  )

  const translationMap = createTranslationMap({
    originalDocs: docIds,
    groupLangIds,
    translationIds
  })

  // Finds all doc links and switches to their locale version that we are translating
  // to, only if those locale versions exist
  return traverseDocument({
    document: data.document
  })({
    transformWidget: ({ content }) => {
      if (isLinkContent(content) && content.value.__TYPE__ === "DocumentLink") {
        return {
          ...content,
          value: {
            __TYPE__: "DocumentLink",
            id: translationMap.get(content.value.id)
          }
        }
      }
      return content
    }
  })
}

function getDocIds(data: ExistingDocumentData) {
  const docLinks = collectWidgets(
    data.document,
    (widget): widget is LinkContent =>
      widget.__TYPE__ === "LinkContent" &&
      widget.value.__TYPE__ === "DocumentLink"
  )

  return Object.values(docLinks).reduce<string[]>(
    (acc, link) =>
      link.value.__TYPE__ === "DocumentLink" ? acc.concat(link.value.id) : acc,
    []
  )
}

function getGroupLangIds(documentIds: string[], status: DocumentVersionStatus) {
  const searchTerm = new URLSearchParams(
    createSearchParams<SearchDocumentNames>([
      { name: "document_id", values: documentIds },
      { name: "status", values: searchDocumentStatus[status] }
    ])
  ).toString()

  return searchDocuments(baseUrl, env.PAGE_BUILDER_AUTH_STRATEGY, searchTerm)
    .then((response) => {
      return response.results.map((result) => ({
        id: result.id,
        groupLangId: result.group_lang_id
      }))
    })
    .catch(() => [])
}

function getTranslationIds(
  groupLangIds: string[],
  localeId: string,
  status: DocumentVersionStatus
) {
  const searchTerm = new URLSearchParams(
    createSearchParams<SearchDocumentNames>([
      { name: "group_lang_id", values: groupLangIds },
      { name: "language", values: [localeId] },
      { name: "status", values: searchDocumentStatus[status] }
    ])
  ).toString()

  return searchDocuments(baseUrl, env.PAGE_BUILDER_AUTH_STRATEGY, searchTerm)
    .then((response) => {
      return response.results.map((result) => ({
        id: result.id,
        groupLangId: result.group_lang_id
      }))
    })
    .catch(() => [])
}

// Creates map of documentIds to documentIds in other locale
export function createTranslationMap({
  originalDocs,
  groupLangIds,
  translationIds
}: {
  originalDocs: string[]
  groupLangIds: { id: string; groupLangId: string }[]
  translationIds: { id: string; groupLangId: string }[]
}): Map<string, string> {
  const groupLangsMap = new Map(groupLangIds.map((lng) => [lng.id, lng]))
  const translatedDocsMap = new Map(
    translationIds.map((doc) => [doc.groupLangId, doc])
  )

  const translatedDocs = new Map<string, string>()
  for (const documentId of originalDocs) {
    const documentLangId = groupLangsMap.get(documentId)
    if (!documentLangId) {
      translatedDocs.set(documentId, documentId)
      continue
    }

    const translatedDocId = translatedDocsMap.get(documentLangId.groupLangId)
    if (!translatedDocId) {
      translatedDocs.set(documentId, documentId)
      continue
    }

    translatedDocs.set(documentId, translatedDocId.id)
  }

  return translatedDocs
}

interface DocAndVersion {
  doc: DocumentMeta
  version: DocumentVersion
}

/**
 * Find the preferred document version from a list of documents (same page in different locales) according to this order:
 *
 * _"unclassified", "published", "archived"_
 */
export function findExistingLocaleVersion(
  documents: DocumentMeta[],
  currentStatus: DocumentVersionStatus
): DocAndVersion | undefined {
  const versions = documents.flatMap((doc) =>
    doc.versions.map((version) => ({ doc, version }))
  )

  // First, try to find an exact match for the current version's status
  const exactVersion = versions.find(
    ({ version }) => version.status === currentStatus
  )

  if (exactVersion) return exactVersion

  const statusPreferenceOrder = [
    "unclassified",
    "published",
    "release",
    "archived"
  ]

  for (const nextBestStatus of statusPreferenceOrder) {
    const version = versions.find(
      ({ version }) => version.status === nextBestStatus
    )

    if (version) return version
  }

  // Couldn't find any interesting versions
  return
}
